/**
 * DataTable — listagem padrão do admin (M1.E4.S2.4 / TH-54207).
 *
 * Wrapper de tabela que segue o padrão visual de painel-screens.jsx:179-258:
 * card com border + radius 8, header opcional (titulo + subtitulo + acao),
 * thead Geist Mono uppercase 10px tracking 0.08em, tbody com borderTop por
 * linha, td 11px 18px, suporte a colunas mono (IDs/timestamps com tabular-nums)
 * e cell renderers customizados (status pill etc.).
 *
 * Ordenacao e paginacao continuam responsabilidade da page; o componente
 * apenas renderiza linhas e estados (loading/erro/vazio fora — passar rows
 * vazio + emptyMessage; para loading/error a page renderiza fora).
 */

import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

export interface DataTableColumn<T> {
  key: string;
  header: string;
  align?: "left" | "right" | "center";
  /** Render customizado da célula. Se omitido, exibe row[key as keyof T] como string. */
  cell?: (row: T) => ReactNode;
  /** Aplica fontFamily mono + tabular-nums. Use para IDs, códigos, timestamps. */
  mono?: boolean;
  className?: string;
  /** Largura opcional da coluna (CSS, ex.: "120px" ou "20%"). */
  width?: string;
}

export interface DataTableProps<T> {
  columns: readonly DataTableColumn<T>[];
  rows: readonly T[];
  /** Cabeçalho opcional do card. */
  title?: string;
  subtitle?: string;
  action?: ReactNode;
  /** Estado vazio. Default: "Nenhum item." */
  emptyMessage?: ReactNode;
  /** Função para extrair key estável da linha (default: idx). */
  rowKey?: (row: T, idx: number) => string;
  className?: string;
}

export function DataTable<T>({
  columns,
  rows,
  title,
  subtitle,
  action,
  emptyMessage = "Nada por aqui.",
  rowKey,
  className,
}: DataTableProps<T>) {
  const hasHeader = Boolean(title || subtitle || action);
  return (
    <div
      className={cn(
        "overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-card)]",
        className,
      )}
    >
      {hasHeader && (
        <div className="flex items-center justify-between border-b border-[var(--color-border)] px-[18px] py-[14px]">
          <div>
            {title ? (
              <div className="text-sm font-semibold">{title}</div>
            ) : null}
            {subtitle ? (
              <div className="mt-0.5 text-[11px] text-[var(--color-muted-foreground)]">
                {subtitle}
              </div>
            ) : null}
          </div>
          {action}
        </div>
      )}
      <table className="w-full border-collapse text-xs">
        <thead className="bg-[var(--color-table-row-tint)]">
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                style={c.width ? { width: c.width } : undefined}
                className={cn(
                  "px-[18px] py-2.5 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--color-muted-foreground)]",
                  c.align === "right"
                    ? "text-right"
                    : c.align === "center"
                      ? "text-center"
                      : "text-left",
                  c.className,
                )}
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                className="px-[18px] py-8 text-center text-[var(--color-muted-foreground)]"
              >
                {emptyMessage}
              </td>
            </tr>
          ) : (
            rows.map((row, idx) => (
              <tr
                key={rowKey?.(row, idx) ?? idx}
                className="border-t border-[var(--color-border)]"
              >
                {columns.map((c) => {
                  const value = c.cell
                    ? c.cell(row)
                    : ((row as Record<string, unknown>)[c.key] as ReactNode);
                  return (
                    <td
                      key={c.key}
                      className={cn(
                        "px-[18px] py-[11px]",
                        c.mono && "font-mono text-[11px] tabular-nums",
                        c.align === "right"
                          ? "text-right"
                          : c.align === "center"
                            ? "text-center"
                            : "text-left",
                        c.className,
                      )}
                    >
                      {value}
                    </td>
                  );
                })}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
